import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import {
  ArrowLeft,
  BarChart3,
  Layers,
  DollarSign,
  TrendingUp,
  Target,
  Building2,
  PieChart,
  ChevronRight,
  Camera,
  Users
} from 'lucide-react';
import { toast } from 'sonner';

const API = BACKEND_URL;

const CAPITAL_COLORS = {
  public: { bg: 'bg-blue-700', text: 'text-blue-700', label: 'Public' },
  private: { bg: 'bg-emerald-700', text: 'text-emerald-700', label: 'Private' },
  philanthropic: { bg: 'bg-purple-700', text: 'text-purple-700', label: 'Philanthropic' },
};

const ImpactDashboard = ({ user }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [snapshotting, setSnapshotting] = useState(false);
  const [trends, setTrends] = useState(null);
  const [evaluations, setEvaluations] = useState([]);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchData(); fetchTrends(); fetchEvaluations(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/impact-dashboard/overview`, { headers });
      setData(res.data);
    } catch (error) {
      console.error('Failed to fetch impact dashboard:', error);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrends = async () => {
    try {
      const res = await axios.get(`${API}/api/impact-dashboard/trends`, { headers });
      setTrends(res.data);
    } catch { setTrends(null); }
  };

  const fetchEvaluations = async () => {
    try {
      const res = await axios.get(`${API}/api/investment-evaluation/evaluations`, { headers });
      const list = Array.isArray(res.data) ? res.data : (res.data.evaluations || []);
      setEvaluations(list);
    } catch { setEvaluations([]); }
  };

  const takeSnapshot = async () => {
    setSnapshotting(true);
    try {
      const res = await axios.post(`${API}/api/impact-dashboard/snapshots`, {}, { headers });
      toast.success(`Snapshot saved for ${res.data.month}`);
      fetchTrends();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to take snapshot');
    } finally {
      setSnapshotting(false);
    }
  };

  const handleSeed = async () => {
    try {
      await axios.post(`${API}/api/impact-dashboard/seed`, {}, { headers });
      toast.success('Impact dashboard data seeded');
      fetchData();
    } catch (error) {
      toast.error('Failed to seed data');
    }
  };

  const formatCurrency = (amount, currency = 'EUR') => {
    if (!amount && amount !== 0) return 'N/A';
    return `${currency} ${new Intl.NumberFormat('el-GR', { maximumFractionDigits: 0 }).format(amount)}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!data || data.total_plans === 0) {
    return (
      <div className="space-y-6 p-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard-btn">
            <ArrowLeft className="h-4 w-4 mr-2" /> Dashboard
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <BarChart3 className="h-7 w-7 text-blue-700" /> Impact Dashboard
            </h1>
            <p className="text-gray-700 mt-1">Executive summary across all blended investment portfolios</p>
          </div>
        </div>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <PieChart className="h-12 w-12 text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No Investment Data Yet</h3>
            <p className="text-gray-700 text-center max-w-md mb-4">Initialize sample blended investment data to see the Impact Dashboard.</p>
            {(user?.role === 'administrator' || user?.role === 'project_manager') && (
              <Button onClick={handleSeed} className="bg-blue-600 hover:bg-blue-700" data-testid="seed-impact-btn">Initialize Sample Data</Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalBudget = data.total_budget || 0;
  const capitalMix = data.capital_mix || {};
  const capitalTotal = Object.values(capitalMix).reduce((a, b) => a + b, 0) || 1;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard-btn">
            <ArrowLeft className="h-4 w-4 mr-2" /> Dashboard
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <BarChart3 className="h-7 w-7 text-blue-700" /> Impact Dashboard
            </h1>
            <p className="text-gray-700 mt-1">Executive summary across all blended investment portfolios</p>
          </div>
        </div>
        <Button variant="outline" onClick={takeSnapshot} disabled={snapshotting} data-testid="snapshot-btn">
          <Camera className="h-4 w-4 mr-2" /> {snapshotting ? 'Saving snapshot…' : 'Take Monthly Snapshot'}
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        {['Overview', 'Trends', 'SROI & Investors'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab.toLowerCase().replace(/ & /g, '-'))}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.toLowerCase().replace(/ & /g, '-')
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-700 hover:text-gray-700'
            }`}
            data-testid={`tab-${tab.toLowerCase().replace(/ & /g, '-')}`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Stats Cards */}
      <section aria-label="Portfolio statistics" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3">
        {[
          { icon: Layers, value: data.total_plans, label: 'Total Plans', color: 'text-blue-700' },
          { icon: PieChart, value: data.total_segments, label: 'Segments', color: 'text-purple-700' },
          { icon: DollarSign, value: formatCurrency(data.total_budget), label: 'Total Budget', color: 'text-emerald-700' },
          { icon: TrendingUp, value: formatCurrency(data.total_allocated), label: 'Allocated', color: 'text-green-700' },
          { icon: Target, value: `${data.allocation_percentage}%`, label: 'Allocation', color: 'text-amber-700' },
          { icon: Building2, value: data.total_investors, label: 'Investors', color: 'text-indigo-700' },
          { icon: Target, value: data.total_kpis, label: 'KPIs', color: 'text-red-700' },
        ].map((stat, i) => (
          <Card key={i} className="bg-gradient-to-br from-white to-gray-50/50">
            <CardContent className="p-3 text-center">
              <stat.icon className={`h-5 w-5 mx-auto mb-1 ${stat.color}`} aria-hidden="true" />
              <span className={`block text-lg font-bold ${stat.color}`}>{stat.value}</span>
              <p className="text-xs text-gray-700">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </section>

      {/* Tab-scoped content ─────────────────────────────────────────────── */}
      {activeTab === 'overview' && (
      <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Capital Mix */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <PieChart className="h-5 w-5 text-blue-700" /> Capital Mix
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Stacked bar */}
            <div className="flex rounded-lg overflow-hidden h-8 mb-4">
              {Object.entries(capitalMix).map(([type, amount]) => {
                const pct = Math.round((amount / capitalTotal) * 100);
                const colors = CAPITAL_COLORS[type] || { bg: 'bg-gray-700', label: type };
                return (
                  <div key={type} className={`${colors.bg} flex items-center justify-center text-white text-xs font-medium`} style={{ width: `${pct}%` }}>
                    {pct}%
                  </div>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-x-8 gap-y-2">
              {Object.entries(capitalMix).map(([type, amount]) => {
                const colors = CAPITAL_COLORS[type] || { bg: 'bg-gray-700', text: 'text-gray-700', label: type };
                return (
                  <div key={type} className="flex items-center gap-2">
                    <div className={`w-2.5 h-2.5 rounded-full ${colors.bg}`} />
                    <div>
                      <p className="text-sm font-medium text-gray-800">{colors.label}</p>
                      <p className="text-xs text-gray-700">{formatCurrency(amount)} ({Math.round((amount / capitalTotal) * 100)}%)</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Status Distribution */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-indigo-700" /> Status Distribution
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">Plans</p>
              {Object.entries(data.plan_statuses || {}).map(([status, count]) => (
                <div key={status} className="flex items-center gap-3 mb-1">
                  <span className="text-sm text-gray-700 w-20 capitalize">{status}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-4">
                    <div className="bg-gray-700 h-4 rounded-full" style={{ width: `${Math.min(100, count * 100 / Math.max(1, data.total_plans))}%` }} />
                  </div>
                  <span className="text-sm font-medium text-gray-700 w-6 text-right">{count}</span>
                </div>
              ))}
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">Segments</p>
              {Object.entries(data.segment_statuses || {}).map(([status, count]) => (
                <div key={status} className="flex items-center gap-3 mb-1">
                  <span className="text-sm text-gray-700 w-20 capitalize">{status}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-4">
                    <div className="bg-amber-700 h-4 rounded-full" style={{ width: `${Math.min(100, count * 100 / Math.max(1, data.total_segments))}%` }} />
                  </div>
                  <span className="text-sm font-medium text-gray-700 w-6 text-right">{count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Investors */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="h-5 w-5 text-indigo-700" /> Top Investors by Allocation
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(data.top_investors || []).map((inv, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="text-sm text-gray-700 w-6">#{i + 1}</span>
                <Building2 className="h-4 w-4 text-indigo-400" />
                <span className="text-sm font-medium text-gray-800 flex-1">{inv.name}</span>
                <div className="w-48 bg-gray-100 rounded-full h-2 hidden md:block">
                  <div className="bg-indigo-500 h-2 rounded-full" style={{ width: `${Math.min(100, (inv.amount / totalBudget) * 100)}%` }} />
                </div>
                <span className="text-sm font-medium text-gray-700">{formatCurrency(inv.amount)}</span>
              </div>
            ))}
            {(data.top_investors || []).length === 0 && <p className="text-sm text-gray-700">No investor data yet</p>}
          </CardContent>
        </Card>

        {/* Financial Instruments */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-teal-700" /> Financial Instruments
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {Object.entries(data.financial_instruments || {}).map(([instrument, amount]) => (
              <div key={instrument}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-800">{instrument}</span>
                  <span className="text-sm font-medium text-teal-700">{formatCurrency(amount)}</span>
                </div>
                <div className="bg-gray-100 rounded-full h-2">
                  <div className="bg-teal-500 h-2 rounded-full" style={{ width: `${Math.min(100, (amount / totalBudget) * 100)}%` }} />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* KPI Progress */}
      {(data.kpis || []).length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Target className="h-5 w-5 text-green-700" /> KPI Progress (Aggregated)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {data.kpis.map((kpi, i) => {
                const pct = kpi.target > 0 ? Math.round((kpi.current / kpi.target) * 100) : 0;
                return (
                  <div key={i} className="border rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-800">{kpi.name}</span>
                      <Badge variant="outline" className="text-xs">1 seg</Badge>
                    </div>
                    <Progress value={pct} className="h-2 mb-1" />
                    <div className="flex justify-between text-xs text-gray-700">
                      <span>{kpi.current} / {kpi.target} {kpi.unit}</span>
                      <span>{pct}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Plan Portfolio */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Layers className="h-5 w-5 text-blue-700" /> Plan Portfolio ({data.total_plans})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {(data.plans || []).map((plan) => (
            <div key={plan.id} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              <Badge variant="outline" className="text-xs capitalize">{plan.status}</Badge>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{plan.title}</p>
                <p className="text-xs text-gray-700 truncate">{plan.investment_title}</p>
              </div>
              <div className="hidden md:flex items-center gap-4 text-xs text-gray-700">
                <span>100%</span>
                <span>{plan.segment_count} seg</span>
              </div>
              <span className="text-sm font-medium text-gray-700">{formatCurrency(plan.total_budget)}</span>
              <div className="w-24 hidden md:block">
                <Progress value={plan.total_budget > 0 ? Math.round((plan.allocated_budget / plan.total_budget) * 100) : 0} className="h-2" />
                <p className="text-xs text-gray-700 text-right mt-0.5">of {formatCurrency(plan.total_budget)}</p>
              </div>
              <ChevronRight className="h-4 w-4 text-gray-700" />
            </div>
          ))}
        </CardContent>
      </Card>
      </>
      )}

      {/* TRENDS TAB ───────────────────────────────────────────────────── */}
      {activeTab === 'trends' && (
        <TrendsView trends={trends} formatCurrency={formatCurrency} />
      )}

      {/* SROI & INVESTORS TAB ─────────────────────────────────────────── */}
      {activeTab === 'sroi-investors' && (
        <SroiInvestorsView
          data={data}
          evaluations={evaluations}
          formatCurrency={formatCurrency}
          totalBudget={totalBudget}
        />
      )}
    </div>
  );
};

export default ImpactDashboard;

// ──────────────────────────────────────────────────────────────────────
// Tab views
// ──────────────────────────────────────────────────────────────────────

const Sparkline = ({ values, color = '#2563eb', height = 36 }) => {
  if (!values || values.length < 2) {
    return <div className="text-xs text-gray-700 italic">Need ≥ 2 snapshots to plot</div>;
  }
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = (max - min) || 1;
  const w = 200, h = height;
  const points = values.map((v, i) => {
    const x = (i / (values.length - 1)) * w;
    const y = h - ((v - min) / range) * h;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="w-full">
      <polyline fill="none" stroke={color} strokeWidth="2" points={points} />
      <circle cx={w} cy={h - ((values[values.length-1] - min) / range) * h} r="3" fill={color} />
    </svg>
  );
};

const TrendsView = ({ trends, formatCurrency }) => {
  if (!trends || !trends.has_history) {
    return (
      <Card>
        <CardContent className="p-10 text-center">
          <Camera className="h-10 w-10 mx-auto text-gray-700 mb-3" />
          <p className="text-base font-semibold text-gray-700">No snapshots yet</p>
          <p className="text-sm text-gray-700 mt-1">
            Click <span className="font-semibold">Take Monthly Snapshot</span> at the top of the page to start
            recording data. Trends will plot here once you have ≥ 2 monthly snapshots.
          </p>
        </CardContent>
      </Card>
    );
  }
  const { months, series, count } = trends;
  const tiles = [
    { key: 'total_budget',       label: 'Total budget',       color: '#047857', format: 'money' },
    { key: 'total_allocated',    label: 'Total allocated',    color: '#047857', format: 'money' },
    { key: 'allocation_pct',     label: 'Allocation %',       color: '#b45309', format: 'pct'   },
    { key: 'kpi_completion_pct', label: 'KPI completion %',   color: '#15803d', format: 'pct'   },
    { key: 'total_plans',        label: 'Plans',              color: '#1d4ed8', format: 'int'   },
    { key: 'total_segments',     label: 'Segments',           color: '#6d28d9', format: 'int'   },
    { key: 'total_investors',    label: 'Investors',          color: '#4338ca', format: 'int'   },
    { key: 'avg_sroi',           label: 'Avg SROI ratio',     color: '#0f766e', format: 'sroi'  },
  ];
  const fmt = (v, f) => {
    if (v == null) return '—';
    if (f === 'money') return formatCurrency(v);
    if (f === 'pct')   return `${v}%`;
    if (f === 'sroi')  return `${(Number(v) || 0).toFixed(2)} : 1`;
    return v;
  };
  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-5 flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-700">Snapshot history</p>
            <p className="text-lg font-semibold text-gray-800">{count} monthly snapshot(s) · {months[0]} → {months[months.length-1]}</p>
          </div>
          <span className="text-xs text-gray-700">Take a snapshot any month to extend the timeline.</span>
        </CardContent>
      </Card>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {tiles.map((t) => {
          const vals = series[t.key] || [];
          const last = vals[vals.length - 1];
          const prev = vals[vals.length - 2];
          const delta = (typeof last === 'number' && typeof prev === 'number') ? last - prev : null;
          const up = delta != null && delta > 0;
          const down = delta != null && delta < 0;
          return (
            <Card key={t.key}>
              <CardContent className="p-4">
                <p className="text-xs uppercase tracking-wide text-gray-700">{t.label}</p>
                <div className="flex items-baseline justify-between mt-1">
                  <span className="block text-xl font-bold" style={{ color: t.color }}>{fmt(last, t.format)}</span>
                  {delta != null && (
                    <span className={`text-xs font-semibold ${up ? 'text-emerald-700' : down ? 'text-red-700' : 'text-gray-700'}`}>
                      {up ? '▲ +' : down ? '▼ ' : '— '}
                      {t.format === 'money' ? formatCurrency(Math.abs(delta))
                        : t.format === 'pct'  ? `${(Math.round(Math.abs(delta) * 10) / 10).toFixed(1)}%`
                        : t.format === 'sroi' ? Math.abs(delta).toFixed(2)
                        : Math.round(Math.abs(delta))}
                    </span>
                  )}
                </div>
                <div className="mt-2"><Sparkline values={vals} color={t.color} /></div>
                <p className="text-xs text-gray-700 mt-1">{months[0]} → {months[months.length-1]}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

const SroiInvestorsView = ({ data, evaluations, formatCurrency, totalBudget }) => {
  const ratios = evaluations
    .map((e) => ({
      title: e.investment_title || e.title || 'Untitled',
      sroi: ((e.sroi || {}).sroi_ratio) || null,
      inputs: ((e.sroi || {}).inputs_cost) || 0,
      net:    ((e.sroi || {}).net_value) || 0,
      gross:  ((e.sroi || {}).total_outcomes_value) || 0,
      outcomes: (((e.sroi || {}).outcomes) || []).length,
      score: e.weighted_total || 0,
      evaluator: e.evaluator_name || e.created_by || '—',
    }))
    .filter((r) => r.sroi);
  const avgSroi = ratios.length ? (ratios.reduce((s, r) => s + r.sroi, 0) / ratios.length) : 0;
  const avgScore = evaluations.length
    ? (evaluations.reduce((s, e) => s + (e.weighted_total || 0), 0) / evaluations.length)
    : 0;

  return (
    <div className="space-y-6">
      {/* SROI headline */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card><CardContent className="p-5">
          <p className="text-xs uppercase tracking-wide text-gray-700">Evaluations with SROI</p>
          <span className="block text-3xl font-bold text-teal-700 mt-1">{ratios.length}<span className="text-sm text-gray-700"> / {evaluations.length}</span></span>
        </CardContent></Card>
        <Card><CardContent className="p-5">
          <p className="text-xs uppercase tracking-wide text-gray-700">Average SROI ratio</p>
          <span className="block text-3xl font-bold text-teal-700 mt-1">{avgSroi.toFixed(2)}<span className="text-sm text-gray-700"> : 1</span></span>
        </CardContent></Card>
        <Card><CardContent className="p-5">
          <p className="text-xs uppercase tracking-wide text-gray-700">Average evaluation score</p>
          <span className="block text-3xl font-bold text-blue-700 mt-1">{avgScore.toFixed(2)}<span className="text-sm text-gray-700"> / 5</span></span>
        </CardContent></Card>
      </div>

      {/* Per-evaluation SROI list */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="h-5 w-5 text-teal-700" /> SROI by Investment</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {ratios.length === 0 && <p className="text-sm text-gray-700">No SROI calculations yet — open an Investment Evaluation to compute one.</p>}
          {ratios.map((r, i) => (
            <div key={i} className="border rounded-lg p-3">
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm font-semibold text-gray-900 truncate flex-1">{r.title}</p>
                <Badge className="bg-teal-100 text-teal-700">{r.sroi.toFixed(2)} : 1</Badge>
              </div>
              <p className="text-xs text-gray-700">
                {formatCurrency(r.inputs)} invested → {formatCurrency(r.gross)} gross → {formatCurrency(r.net)} net · {r.outcomes} outcomes ·
                evaluator {r.evaluator} · score {r.score.toFixed(2)}/5
              </p>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Top investors carried over from overview but enriched */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Building2 className="h-5 w-5 text-indigo-700" /> Top Investors by Allocation</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {(data?.top_investors || []).map((inv, i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="text-sm text-gray-700 w-6">#{i + 1}</span>
              <Building2 className="h-4 w-4 text-indigo-400" />
              <span className="text-sm font-medium text-gray-800 flex-1">{inv.name}</span>
              <div className="w-48 bg-gray-100 rounded-full h-2 hidden md:block">
                <div className="bg-indigo-500 h-2 rounded-full" style={{ width: `${Math.min(100, (inv.amount / Math.max(1, totalBudget)) * 100)}%` }} />
              </div>
              <span className="text-sm font-medium text-gray-700">{formatCurrency(inv.amount)}</span>
            </div>
          ))}
          {(data?.top_investors || []).length === 0 && <p className="text-sm text-gray-700">No investor data yet</p>}
        </CardContent>
      </Card>
    </div>
  );
};
